package no.vareflow.web

import no.vareflow.app.domain.*

fun WebApp.renderOverview(root: El, user: UserAccount) {
    val open = openSessions()
    val session = shownOpenSession()
    // A closed month has moved to Historikk: keep the selection on an open month.
    if (session != null && core.currentInventorySession()?.id != session.id) core.selectInventorySession(session.id)
    root.pageHeader(t("Oversikt", "Overview"), t("Åpne måneder, oppgaver og fremdrift", "Open months, tasks and progress"))
    if (session == null) {
        root.emptyCard(
            if (core.inventorySessions.isEmpty()) t("Ingen varetelling er opprettet ennå.", "No stock count has been created yet.")
            else t("Ingen åpen måned. Avsluttede måneder finner du under Analyse → Historikk.", "No open month. Closed months are under Analysis → History.")
        )
        if (user.role == Role.ADMIN) {
            root.button(if (core.inventorySessions.isEmpty()) t("Opprett første måned", "Create first month") else t("+ Ny måned", "+ New month"), "btn primary block") {
                showCreateSession(user, core.inventorySessions.firstOrNull()?.locationName)
            }
        }
        return
    }
    root.monthTabs(this, open, session)
    val seesAll = user.role == Role.ADMIN || AccessRules.hasPermission(user, Permission.INVENTORY_VIEW_OTHER)
    root.div("card session-card") {
        val assignments = core.assignmentsForSession(session.id)
        val visible = if (seesAll) assignments else assignments.filter { user.id in it.assigneeIds }
        val total = visible.sumOf { core.totalToCount(session.id, it.category) }
        val counted = visible.sumOf { core.countedCount(session.id, it.category) }
        val percent = if (total == 0) 0 else counted * 100 / total
        // A Hovedvare counts as done when every product in it is counted (no Ferdig status any more).
        val doneCount = visible.count { a -> core.totalToCount(session.id, a.category).let { t -> t > 0 && core.countedCount(session.id, a.category) >= t } }
        val donePercent = if (visible.isEmpty()) 0 else doneCount * 100 / visible.size
        if (modernUi) {
            div("session-modern") {
                progressRing(this@renderOverview, "overview:${session.id}", percent, donePercent)
                div("session-info") {
                    div("row") {
                        div("grow") {
                            div("session-month", formatYearMonth(session.yearMonth))
                            div("muted", session.locationName)
                        }
                        inventoryStatusBadge(session.status, true)
                    }
                    div("legend-row") {
                        span("legend-dot dot-orange")
                        span("small", t("$counted av $total produkter telt", "$counted of $total products counted"))
                    }
                    div("legend-row") {
                        span("legend-dot dot-blue")
                        span("small", t("$doneCount av ${visible.size} hovedvarer helt telt", "$doneCount of ${visible.size} main categories fully counted"))
                    }
                    div("sync-note", syncNote())
                }
            }
        } else {
            div("row") {
                div("grow") {
                    div("session-month", formatYearMonth(session.yearMonth))
                    div("muted", session.locationName)
                }
                statusBadge(session.status.label, inventoryStatusTone(session.status))
            }
            div("strong", t("$counted av $total produkter telt · $percent %", "$counted of $total products counted · $percent %"))
            div("progress") { div("progress-bar").attr("style", "width: $percent%") }
            div("muted small", t("$doneCount av ${visible.size} hovedvarer helt telt", "$doneCount of ${visible.size} main categories fully counted"))
            div("sync-note", syncNote())
        }

        if (session.status == InventoryStatus.ACTIVE) {
            button(t("Åpne varetelling", "Open stock count"), "btn primary block cta") { select(Tab.TELLING) }
        }
        val canAssign = AccessRules.hasPermission(user, Permission.INVENTORY_ASSIGN_CATEGORY)
        val canClose = AccessRules.hasPermission(user, Permission.INVENTORY_CLOSE)
        val isAdmin = user.role == Role.ADMIN
        if (user.role == Role.ADMIN || canAssign || canClose) {
            div("button-row") {
                when (session.status) {
                    InventoryStatus.DRAFT -> {
                        if (canAssign) button(t("Fordel arbeid", "Assign work"), "btn outline") { showAssignments(session, user) }
                        if (canClose) button(t("Aktiver", "Activate"), "btn primary") {
                            if (core.activateInventorySession(session.id, user)) toast(t("${formatYearMonth(session.yearMonth)} er aktivert.", "${formatYearMonth(session.yearMonth)} is activated."))
                            else toast(t("Kan ikke aktivere: denne lokasjonen har allerede en aktiv telling.", "Cannot activate: this location already has an active count."))
                        }
                    }
                    InventoryStatus.ACTIVE -> {
                        if (canAssign) button(t("Fordel arbeid", "Assign work"), "btn outline") { showAssignments(session, user) }
                        if (isAdmin) button(t("Avslutt", "Close"), "btn primary") { confirmClose(session, user) }
                    }
                    InventoryStatus.CLOSED, InventoryStatus.ARCHIVED -> Unit
                }
            }
            if (user.role == Role.ADMIN) button(t("+ Ny måned / lokasjon", "+ New month / location"), "btn outline block") { showCreateSession(user, session.locationName) }
        }
    }

    val visibleAssignments = core.assignmentsForSession(session.id).filter { seesAll || user.id in it.assigneeIds }
        .sortedBy { core.orderedCategories().indexOf(it.category) }
    if (visibleAssignments.isEmpty()) {
        root.emptyCard(if (user.role == Role.ADMIN) t("Ingen kategorier i tellingen.", "No categories in the count.") else t("Du har ingen tildelte kategorier i denne tellingen.", "You have no assigned categories in this count."))
        return
    }
    root.h2(t("Kategorier", "Categories"), "section-title")
    root.div("card-grid") {
        visibleAssignments.forEach { assignment ->
            val counted = core.countedCount(session.id, assignment.category)
            val total = core.totalToCount(session.id, assignment.category)
            val assignees = assignment.assigneeIds.mapNotNull { id -> core.userById(id)?.name }
            div("card category-card") {
                if (modernUi) {
                    val remaining = (total - counted).coerceAtLeast(0)
                    val tone = when {
                        total > 0 && counted >= total -> "green"
                        counted > 0 -> "amber"
                        else -> "orange"
                    }
                    div("card-title", assignment.category)
                    sparkline(ProgressTimeline.activity(core.auditEvents, session.id, assignment.category, buckets = 12), tone)
                    div("row") {
                        div("grow") {
                            div("small strong", t("$counted / $total telt", "$counted / $total counted"))
                            div("muted tiny", if (assignees.isEmpty()) t("Ikke tildelt", "Not assigned") else assignees.joinToString(", "))
                        }
                        span("muted tiny", if (remaining == 0) t("Alt telt", "All counted") else t("$remaining igjen", "$remaining left"))
                    }
                    div("mini-progress") {
                        div("mini-fill fill-$tone").attr("style", "width: ${if (total == 0) 0 else counted * 100 / total}%")
                    }
                } else {
                div("row") {
                    div("grow") {
                        div("card-title", assignment.category)
                        div("muted small", t("$counted / $total telt", "$counted / $total counted"))
                        div("muted tiny", if (assignees.isEmpty()) t("Ikke tildelt", "Not assigned") else assignees.joinToString(", "))
                    }
                }
                }
            }
        }
    }
}

/** Month tabs (Utkast/Aktiv only), e.g. "September 2026 | Oktober 2026". */
private fun El.monthTabs(app: WebApp, open: List<InventorySession>, shown: InventorySession) {
    val months = open.groupingBy { it.yearMonth }.eachCount()
    div("month-tabs") {
        attr("role", "tablist")
        attr("aria-label", t("Åpne måneder", "Open months"))
        attr("data-scroll-key", "month-tabs")
        open.forEach { s ->
            val selected = s.id == shown.id
            el("button", "month-tab" + if (selected) " selected" else "") {
                attr("type", "button")
                attr("role", "tab")
                attr("aria-selected", selected.toString())
                span("month-tab-name", formatYearMonth(s.yearMonth))
                // Two locations in the same month: tell them apart.
                if ((months[s.yearMonth] ?: 0) > 1) span("month-tab-sub", s.locationName)
                span("month-tab-status status-" + s.status.name.lowercase(), s.status.label)
                onClick { if (!selected) app.core.selectInventorySession(s.id) }
            }
        }
    }
}

private fun WebApp.confirmClose(session: InventorySession, user: UserAccount) {
    val totals = liveTotals(session, user)
    val canSeePrice = AccessRules.hasPermission(user, Permission.PRODUCT_VIEW_PRICE)
    openDialog(t("Avslutt ${formatYearMonth(session.yearMonth)}?", "Close ${formatYearMonth(session.yearMonth)}?")) { close ->
        p(t("Er du sikker? Måneden låses og flyttes til Historikk (Analyse → Historikk).", "Are you sure? The month is locked and moved to History (Analysis → History)."))
        div("close-summary") {
            detailRow(t("Telte produkter", "Counted products"), t("${totals.counted} av ${totals.total}", "${totals.counted} of ${totals.total}"))
            if (canSeePrice) detailRow(t("Beregnet vareverdi", "Calculated stock value"), "${formatPrice(totals.value)} kr")
        }
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(t("Ja, avslutt", "Yes, close"), "btn primary") {
                close()
                if (core.closeInventorySession(session.id, user) == null) {
                    toast(t("Måneden kunne ikke avsluttes. Bare en aktiv måned kan avsluttes.", "The month could not be closed. Only an active month can be closed."))
                } else {
                    shownOpenSession()?.let { core.selectInventorySession(it.id) }
                    toast(t("${formatYearMonth(session.yearMonth)} er avsluttet og ligger nå i Historikk.", "${formatYearMonth(session.yearMonth)} is closed and is now in History."))
                }
            }
        }
    }
}

private fun WebApp.showCreateSession(user: UserAccount, defaultLocation: String?) {
    val state = object {
        var yearMonth = nextSuggestedYearMonth(core.inventorySessions.map { it.yearMonth })
        var location = defaultLocation ?: DomainDefaults.DEMO_LOCATION_NAME
        var error: String? = null
        /** The month that already exists, with what can be done about it. */
        var existing: InventorySession? = null
    }
    openDialog(t("Ny månedlig varetelling", "New monthly stock count")) { close ->
        field(t("Måned (ÅÅÅÅ-MM)", "Month (YYYY-MM)"), state.yearMonth, "dlg-ym") { state.yearMonth = it }
        field(t("Lokasjon / lager", "Location / store"), state.location, "dlg-loc") { state.location = it }
        p(t("Nye tellinger opprettes som Utkast og aktiveres av admin.", "New counts are created as Draft and activated by an admin."), "muted tiny")
        errorText(state.error)
        val existing = state.existing
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            when {
                existing == null -> {}
                existing.status == InventoryStatus.CLOSED && user.role == Role.ADMIN -> button(t("Åpne igjen", "Reopen"), "btn outline") {
                    if (core.reopenInventorySession(existing.id, user)) {
                        close()
                        toast(t("${formatYearMonth(existing.yearMonth)} er åpnet igjen.", "${formatYearMonth(existing.yearMonth)} has been reopened."))
                    }
                }
                existing.status != InventoryStatus.CLOSED -> button(t("Vis måneden", "Show the month"), "btn outline") {
                    core.selectInventorySession(existing.id); close()
                }
            }
            button(t("Opprett", "Create"), "btn primary") {
                val created = core.createInventorySession(state.yearMonth, state.location, user)
                if (created == null) {
                    val ym = state.yearMonth.trim()
                    val found = core.existingSessionFor(ym, state.location)
                    state.existing = found
                    state.error = when {
                        YearMonthValue.parse(ym) == null -> t("Skriv måneden som ÅÅÅÅ-MM, f.eks. 2026-10.", "Write the month as YYYY-MM, e.g. 2026-10.")
                        state.location.isBlank() -> t("Skriv inn lokasjon / lager.", "Enter the location / store.")
                        found == null -> t("Tellingen kunne ikke opprettes.", "The count could not be created.")
                        found.status == InventoryStatus.CLOSED -> t(
                            "${formatYearMonth(found.yearMonth)} · ${found.locationName} finnes allerede, men er avsluttet" +
                                (found.closedAt?.let { " (${formatDateTime(it)})" } ?: "") + ". Avsluttede måneder vises ikke i fanene." +
                                if (user.role == Role.ADMIN) " Du kan åpne den igjen." else " En admin kan åpne den igjen.",
                            "${formatYearMonth(found.yearMonth)} · ${found.locationName} already exists but is closed" +
                                (found.closedAt?.let { " (${formatDateTime(it)})" } ?: "") + ". Closed months are not shown in the tabs." +
                                if (user.role == Role.ADMIN) " You can reopen it." else " An admin can reopen it.")
                        else -> t("${formatYearMonth(found.yearMonth)} · ${found.locationName} finnes allerede (${found.status.label}).",
                            "${formatYearMonth(found.yearMonth)} · ${found.locationName} already exists (${found.status.label}).")
                    }
                    requestRender()
                } else {
                    close()
                    toast(t("${formatYearMonth(created.yearMonth)} er opprettet som Utkast.", "${formatYearMonth(created.yearMonth)} has been created as Draft."))
                }
            }
        }
    }
}

/** Sync line on the month card: local test, or the Firebase state with the queue. */
private fun WebApp.syncNote(): String =
    if (!cloudMode) t("Synkstatus: lokal test i nettleseren", "Sync status: local test in the browser")
    else t("Synkstatus", "Sync status") + ": ${cloud.state.label}" + if (cloud.pendingWrites > 0) " · ${cloud.pendingWrites} " + t("i kø", "queued") else ""
